import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, RefreshCw, TrendingUp, Building2, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import { formatDateToISO } from '../../utils/formatters';
import { getConsolidatedPLReport, downloadConsolidatedPLPDF } from '../../api/reports';
import BlurredValue from '../../components/common/BlurredValue';
import ReportPreviewModal from '../../components/reports/ReportPreviewModal';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ConsolidatedPLReport = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const now = new Date();
  const [dateRange, setDateRange] = useState({
    start: formatDateToISO(new Date(now.getFullYear(), now.getMonth(), 1)),
    end: formatDateToISO(new Date(now.getFullYear(), now.getMonth() + 1, 0))
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getConsolidatedPLReport(dateRange);
      if (response.success) {
        setReport(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch P&L report');
      }
    } catch (error) {
      console.error('Error fetching consolidated P&L:', error);
      toast.error(error.message || 'Failed to fetch consolidated P&L');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      await downloadConsolidatedPLPDF(dateRange.start, dateRange.end);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const getDateRangeLabel = () => {
    const start = new Date(dateRange.start).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    const end = new Date(dateRange.end).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    return `${start} - ${end}`;
  };

  const handleWhatsApp = async () => {
    if (!report) return;
    setSendingWhatsApp(true);
    try {
      const netProfitValue = report.summary?.net_profit || 0;
      const isProfitable = netProfitValue >= 0;
      const message = encodeURIComponent(
        `📊 *Consolidated P&L Report*\n` +
          `📅 Period: ${getDateRangeLabel()}\n\n` +
          `💰 Revenue: ${formatCurrency(report.summary?.total_revenue)}\n` +
          `📦 COGS: ${formatCurrency(report.summary?.total_cogs)}\n` +
          `📈 Gross Profit: ${formatCurrency(report.summary?.gross_profit)}\n` +
          `👥 Payroll: ${formatCurrency(report.summary?.total_payroll)}\n` +
          `${isProfitable ? '✅' : '❌'} Net ${isProfitable ? 'Profit' : 'Loss'}: ${formatCurrency(Math.abs(netProfitValue))}\n` +
          `🏢 Branches: ${report.by_branch?.length || 0}`
      );
      window.open(`https://wa.me/?text=${message}`, '_blank');
      toast.success('Opening WhatsApp...');
    } catch (error) {
      toast.error('Failed to open WhatsApp');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Report content for modal
  const ReportContent = () => {
    if (!report) return null;

    return (
      <div>
        {/* P&L Statement */}
        <h3 className="font-semibold mb-4 text-lg">Profit & Loss Statement</h3>

        <table className="w-full text-sm border-collapse mb-6">
          <tbody>
            <tr className="bg-green-50">
              <td className="border border-gray-300 px-4 py-3 font-semibold">Total Revenue</td>
              <td className="border border-gray-300 px-4 py-3 text-right font-bold text-green-600">
                {formatCurrency(report.summary.total_revenue)}
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 px-4 py-2 pl-8 text-gray-600">
                Less: Cost of Goods Sold
              </td>
              <td className="border border-gray-300 px-4 py-2 text-right text-red-600">
                ({formatCurrency(report.summary.total_cogs)})
              </td>
            </tr>
            <tr className="bg-gray-50">
              <td className="border border-gray-300 px-4 py-3 font-semibold">Gross Profit</td>
              <td className="border border-gray-300 px-4 py-3 text-right font-semibold">
                {formatCurrency(report.summary.gross_profit)}
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 px-4 py-2 pl-8 text-gray-600">
                Less: Payroll Expense
              </td>
              <td className="border border-gray-300 px-4 py-2 text-right text-red-600">
                ({formatCurrency(report.summary.payroll_expense)})
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 px-4 py-2 pl-8 text-gray-600">
                Less: Employer Pension
              </td>
              <td className="border border-gray-300 px-4 py-2 text-right text-red-600">
                ({formatCurrency(report.summary.employer_pension)})
              </td>
            </tr>
            <tr className="bg-red-50">
              <td className="border border-gray-300 px-4 py-3 font-semibold">Total Expenses</td>
              <td className="border border-gray-300 px-4 py-3 text-right font-semibold text-red-600">
                {formatCurrency(report.summary.total_expenses)}
              </td>
            </tr>
            <tr className={isProfit ? 'bg-green-100' : 'bg-red-100'}>
              <td className="border border-gray-300 px-4 py-3 font-bold text-lg">
                Net {isProfit ? 'Profit' : 'Loss'}
              </td>
              <td
                className={`border border-gray-300 px-4 py-3 text-right font-bold text-xl ${isProfit ? 'text-green-700' : 'text-red-700'}`}
              >
                {formatCurrency(Math.abs(netProfit))}
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 px-4 py-2 text-gray-600">Profit Margin</td>
              <td className="border border-gray-300 px-4 py-2 text-right font-semibold">
                {report.summary.profit_margin_percent?.toFixed(1)}%
              </td>
            </tr>
          </tbody>
        </table>

        {/* Branch Breakdown */}
        {report.by_branch?.length > 0 && (
          <>
            <h3 className="font-semibold mb-4 text-lg">Performance by Branch</h3>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2 text-left">Branch</th>
                  <th className="border border-gray-300 px-4 py-2 text-center">Sales</th>
                  <th className="border border-gray-300 px-4 py-2 text-right">Revenue</th>
                  <th className="border border-gray-300 px-4 py-2 text-right">COGS</th>
                  <th className="border border-gray-300 px-4 py-2 text-right">Gross Profit</th>
                </tr>
              </thead>
              <tbody>
                {report.by_branch.map((branch) => (
                  <tr key={branch.branch_id} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2 font-medium">
                      {branch.branch_name}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      {branch.sale_count}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      {formatCurrency(branch.revenue)}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right text-red-600">
                      ({formatCurrency(branch.cogs)})
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right font-semibold text-green-600">
                      {formatCurrency(branch.gross_profit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/reports')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-primary-black">
                  Consolidated P&L
                </h1>
                <p className="text-sm text-gray-500">Profit & Loss across all branches</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="px-3 py-2 border rounded-lg"
              />
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="px-3 py-2 border rounded-lg"
              />
              <button onClick={fetchReport} className="p-2 border rounded-lg hover:bg-gray-50">
                <RefreshCw className="w-5 h-5" />
              </button>
              {report && (
                <button
                  onClick={() => setShowReportModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark"
                >
                  <FileText className="w-4 h-4" />
                  Generate Report
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner message="Generating consolidated report..." className="py-12" />
        ) : report ? (
          <>
            {/* P&L Statement */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-green-600" />
                Profit & Loss Statement
              </h3>

              <div className="space-y-3">
                {/* Revenue */}
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="font-medium">Total Revenue</span>
                  <span className="text-xl font-bold text-green-600">
                    <BlurredValue value={formatCurrency(report.summary.total_revenue)} />
                  </span>
                </div>

                {/* COGS */}
                <div className="flex justify-between items-center py-2 pl-4">
                  <span className="text-gray-600">Cost of Goods Sold</span>
                  <span className="text-red-600">
                    <BlurredValue value={`(${formatCurrency(report.summary.total_cogs)})`} />
                  </span>
                </div>

                {/* Gross Profit */}
                <div className="flex justify-between items-center py-2 border-b bg-gray-50 px-4 rounded">
                  <span className="font-medium">Gross Profit</span>
                  <span className="font-semibold">
                    <BlurredValue value={formatCurrency(report.summary.gross_profit)} />
                  </span>
                </div>

                {/* Operating Expenses */}
                <div className="flex justify-between items-center py-2 pl-4">
                  <span className="text-gray-600">Payroll Expense</span>
                  <span className="text-red-600">
                    <BlurredValue value={`(${formatCurrency(report.summary.payroll_expense)})`} />
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 pl-4">
                  <span className="text-gray-600">Employer Pension</span>
                  <span className="text-red-600">
                    <BlurredValue value={`(${formatCurrency(report.summary.employer_pension)})`} />
                  </span>
                </div>

                {/* Total Expenses */}
                <div className="flex justify-between items-center py-2 border-b bg-red-50 px-4 rounded">
                  <span className="font-medium">Total Expenses</span>
                  <span className="font-semibold text-red-600">
                    <BlurredValue value={formatCurrency(report.summary.total_expenses)} />
                  </span>
                </div>

                {/* Net Profit */}
                <div
                  className={`flex justify-between items-center py-3 px-4 rounded-lg ${isProfit ? 'bg-green-100' : 'bg-red-100'}`}
                >
                  <span className="font-bold text-lg">Net {isProfit ? 'Profit' : 'Loss'}</span>
                  <span
                    className={`text-2xl font-bold ${isProfit ? 'text-green-700' : 'text-red-700'}`}
                  >
                    <BlurredValue value={formatCurrency(Math.abs(netProfit))} />
                  </span>
                </div>

                {/* Margin */}
                <div className="flex justify-between items-center py-2 text-sm text-gray-600">
                  <span>Profit Margin</span>
                  <span className="font-semibold">
                    {report.summary.profit_margin_percent.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>

            {/* Branch Breakdown */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Performance by Branch
              </h3>
              <div className="space-y-4">
                {report.by_branch?.map((branch) => {
                  const revenuePercent = (parseFloat(branch.revenue) / maxRevenue) * 100;
                  return (
                    <div key={branch.branch_id} className="p-3 border rounded-lg hover:bg-gray-50">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">{branch.branch_name}</span>
                        <span className="font-semibold text-accent-gold">
                          <BlurredValue value={formatCurrency(branch.revenue)} />
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-accent-gold to-yellow-400 transition-all"
                          style={{ width: `${revenuePercent}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>{branch.sale_count} sales</span>
                        <span>
                          Profit: <BlurredValue value={formatCurrency(branch.gross_profit)} />
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Monthly Trend */}
            {report.monthly_trend?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Monthly Trend
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Month</th>
                        <th className="px-4 py-2 text-center text-sm font-semibold">Sales</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold">Revenue</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold">Profit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {report.monthly_trend.map((month, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">
                            {new Date(month.month).toLocaleDateString('en-US', {
                              month: 'short',
                              year: 'numeric'
                            })}
                          </td>
                          <td className="px-4 py-3 text-center">{month.sale_count}</td>
                          <td className="px-4 py-3 text-right">
                            <BlurredValue value={formatCurrency(month.revenue)} />
                          </td>
                          <td
                            className={`px-4 py-3 text-right font-semibold ${parseFloat(month.profit) >= 0 ? 'text-green-600' : 'text-red-600'}`}
                          >
                            <BlurredValue value={formatCurrency(month.profit)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* Report Preview Modal */}
      <ReportPreviewModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Consolidated Profit & Loss Report"
        subtitle={getDateRangeLabel()}
        onDownload={handleDownloadPDF}
        onWhatsApp={handleWhatsApp}
        downloading={downloading}
        sendingWhatsApp={sendingWhatsApp}
      >
        <ReportContent />
      </ReportPreviewModal>
    </div>
  );
};

export default ConsolidatedPLReport;
